import { api } from '@goaltree/backend/convex/_generated/api'
import { PRO_TRIAL_DAYS } from '@goaltree/backend/convex/lib/plan'
import { cn } from '@goaltree/ui/lib/utils'
import { useMutation } from 'convex/react'
import { Sparkles } from 'lucide-react'
import { usePostHog } from 'posthog-js/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { UpgradeDialog } from '@/components/upgrade-dialog'
import { useIsPro } from '@/hooks/use-is-pro'

// Without `onUpgrade` the card hosts its own paywall; pass it when the card lives
// in a sheet that closes (and unmounts) before the paywall opens.
export function GoProCard({
  onUpgrade,
  compact,
  className,
}: {
  onUpgrade?: () => void
  compact?: boolean
  className?: string
}) {
  const { t } = useTranslation()
  const { isPro, isLoading, trialEligible } = useIsPro()
  const startTrial = useMutation(api.subscriptions.startMyProTrial)
  const posthog = usePostHog()
  const [busy, setBusy] = useState(false)
  const [upgradeOpen, setUpgradeOpen] = useState(false)

  if (isPro || isLoading) return null

  async function handleClick() {
    if (!trialEligible) {
      if (onUpgrade) onUpgrade()
      else setUpgradeOpen(true)
      return
    }
    setBusy(true)
    const res = await startTrial({}).catch(() => null)
    setBusy(false)
    if (!res?.started) {
      toast.error(t('trial.startError'))
      return
    }
    posthog.capture('pro_trial_started', { reason: 'sidebar' })
    toast.success(t('trial.started'))
  }

  return (
    <>
      {!onUpgrade && (
        <UpgradeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} reason="pro" />
      )}
      <button
        type="button"
        onClick={handleClick}
        disabled={busy}
        className={cn(
          'flex w-full cursor-pointer items-center gap-3 rounded-xl border border-primary/25 bg-linear-to-br from-primary/18 via-primary/8 to-primary/3 text-left transition-colors hover:from-primary/24 hover:via-primary/12 disabled:opacity-60',
          compact ? 'px-2.5 py-2' : 'px-3 py-3',
          className,
        )}
      >
        <span
          className={cn(
            'flex shrink-0 items-center justify-center rounded-lg bg-background/80 text-primary-text shadow-xs',
            compact ? 'size-7' : 'size-8',
          )}
        >
          <Sparkles className={compact ? 'size-3.5' : 'size-4'} />
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={cn(
              'block font-bold leading-tight text-foreground',
              compact ? 'text-[13px]' : 'text-sm',
            )}
          >
            {trialEligible ? t('nav.goProTrial', { days: PRO_TRIAL_DAYS }) : t('nav.goPro')}
          </span>
          <span
            className={cn(
              'mt-0.5 block leading-snug text-foreground/75',
              compact ? 'text-[11px]' : 'text-xs',
            )}
          >
            {t('nav.goProValue')}
          </span>
        </span>
      </button>
    </>
  )
}
